import { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';

// Drives a polite aria-live region for a course run. Sparse on purpose: an
// 84-cell run at a 1.5s poll must not become 84 utterances, so it speaks only at
// quartile milestones, the first failure, and the terminal tally (kickoff is the
// publish toast's job).

function quartile(ready: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.floor((ready / total) * 4);
}

export function useCourseTranslationAnnouncement({
  ready,
  total,
  failed,
  running,
}: {
  ready: number;
  total: number;
  failed: number;
  running: boolean;
}): string {
  const [message, setMessage] = useState('');
  // Only announce a terminal state for a run we actually saw running, so mounting
  // on an already-complete course stays silent.
  const sawRunning = useRef(false);
  const lastQuartile = useRef<number | null>(null);
  const announcedFailure = useRef(false);

  useEffect(() => {
    if (running) {
      sawRunning.current = true;

      if (failed > 0 && !announcedFailure.current) {
        announcedFailure.current = true;
        setMessage(t`Some translations failed to generate.`);
        return;
      }

      const current = quartile(ready, total);
      if (lastQuartile.current === null) {
        lastQuartile.current = current;
        return;
      }
      if (current > lastQuartile.current) {
        lastQuartile.current = current;
        setMessage(t`${ready} of ${total} translations ready.`);
      }
      return;
    }

    // Settled. Announce the outcome once for a run we watched, then reset so the
    // next run starts clean.
    if (sawRunning.current) {
      sawRunning.current = false;
      lastQuartile.current = null;
      announcedFailure.current = false;
      setMessage(
        failed > 0
          ? t`${ready} of ${total} translations ready, ${failed} failed.`
          : t`All ${total} translations ready.`,
      );
    }
  }, [ready, total, failed, running]);

  return message;
}
